import { cn } from "@/lib/utils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  ChangeApplyStatus,
  MessageContext,
} from "@/store/zustand/supabaseStore";
import React, { useEffect, useCallback, useMemo } from "react";
import { EngSpecFileDiff } from "../EngSpec/EngSpec";
import { useCombinedFileChanges } from "./useApplyChatBlock";
import useActivityStore from "@/store/zustand/activityStore";
import { useSendMessage } from "./useSendMessage";
import { useAiActionStore, AI_MODEL } from "@/store/zustand/aiActionStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { StoryCodeActionButtons } from "./StoryCodeActionButtons";
import { FilepathLink } from "./FilepathLink";

interface ChangeStatusIconProps {
  changeStatus:
    | ChangeApplyStatus
    | { status: "generating" }
    | { status: "unknown" };
}

const ChangeStatus = ({ changeStatus }: ChangeStatusIconProps) => {
  const getStatusContent = () => {
    if (changeStatus.status === "failed" && changeStatus.fixing) {
      return {
        icon: (
          <div className="size-4 flex flex-col items-center justify-center">
            <img
              src="/icons/ai-chat-status/loader-circle.svg"
              className="size-4 shrink-0 flex animate-spin"
              alt="Generating"
            />
          </div>
        ),
        tooltip: "Generating",
      };
    }

    switch (changeStatus.status) {
      case "failed":
        return {
          icon: (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/failed.svg"
                className="size-4 shrink-0"
                alt="Failed"
              />
            </div>
          ),
          tooltip: "Error applying change",
        };
      case "generating":
        return {
          icon: (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/loader-circle.svg"
                className="size-4 shrink-0 flex animate-spin"
                alt="Generating"
              />
            </div>
          ),
          tooltip: "Generating",
        };
      case "succeeded":
        return {
          icon: (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/success.svg"
                className="size-4 shrink-0 flex"
                alt="Applied"
              />
            </div>
          ),
          tooltip: "Applied",
        };
      case "in_progress":
        return {
          icon: (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/progress.svg"
                className="size-4 shrink-0 flex animate-pulse"
                alt="Applying"
              />
            </div>
          ),
          tooltip: "Applying",
        };
      case "pending":
        return {
          icon: (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/pending.svg"
                className="size-4 shrink-0 flex animate-pulse"
                alt="Pending"
              />
            </div>
          ),
          tooltip: "Pending",
        };
      default:
        return {
          icon: (
            <div className="size-4 flex flex-col items-center justify-center">
              <img
                src="/icons/ai-chat-status/progress.svg"
                className="size-4 shrink-0 flex"
                alt="Status"
              />
            </div>
          ),
          tooltip: "",
        };
    }
  };

  const { icon, tooltip } = getStatusContent();

  return (
    <div>
      <div
        data-tooltip-id="tooltip"
        data-tooltip-place="top-start"
        data-tooltip-content={tooltip}
      >
        {icon}
      </div>
    </div>
  );
};

export interface CommitProps {
  changes: EngSpecFileDiff[];
  messageContext?: MessageContext;
  loadingAiChange: boolean;
  openAiChatEngSpec: (change: EngSpecFileDiff) => void;
}

export const ChatAiCodeChanges = React.memo(
  ({
    changes,
    loadingAiChange,
    messageContext,
    openAiChatEngSpec,
  }: CommitProps) => {
    const activityStreamList = useActivityStore(
      (state) => state.activityStreamList,
    );

    const fileToContentsFromApi = useCodebaseStore(
      (state) => state.fileToContents,
    );

    const combinedFileChanges = useCombinedFileChanges(
      changes,
      loadingAiChange,
    );

    const savedActivity = useMemo(() => {
      return (
        messageContext?.savedActivityId === "RAN_COMMAND" ||
        activityStreamList?.find?.(
          (activity) =>
            messageContext?.savedActivityId === activity.id &&
            !activity.change_undone,
        )
      );
    }, [activityStreamList, messageContext?.savedActivityId]);

    const { sendMessage } = useSendMessage();
    const { codeGenerationModel, agent } = useAiActionStore();
    const selectedConversationId = useAiActionStore(
      (state) => state.selectedConversationId,
    );
    const aiChatMessages = useSupabaseStore((state) => state.aiChatMessages);

    useEffect(() => {
      const filesToReload = new Set<string>();
      if (!changes || !Array.isArray(changes)) {
        return;
      }

      changes
        .map((change) => change?.path)
        .filter((filename) => Boolean(filename))
        .forEach((filename: string) => {
          if (fileToContentsFromApi[filename] === undefined) {
            filesToReload.add(filename);
          }
        });

      if (filesToReload.size > 0) {
        useCodebaseStore
          .getState()
          .setReloadFiles({ files: Array.from(filesToReload) });
      }
    }, [changes, fileToContentsFromApi]);

    const getChangeStatus = (
      change: EngSpecFileDiff,
      index: number,
    ): ChangeApplyStatus | { status: "generating" } | { status: "unknown" } => {
      if (loadingAiChange) {
        if (index === combinedFileChanges?.length - 1) {
          return { status: "generating" };
        }

        return { status: "pending" };
      }

      if (messageContext?.changeApplyStatus) {
        if (
          change.deployEdgeFunction &&
          messageContext.changeApplyStatus.deployEdgeFunction
        ) {
          return messageContext.changeApplyStatus.deployEdgeFunction;
        }

        if (
          change.runConvexSync &&
          messageContext.changeApplyStatus.runConvexSync
        ) {
          return messageContext.changeApplyStatus.runConvexSync;
        }

        if (
          change.runSqlMigration &&
          messageContext.changeApplyStatus.runSqlMigration
        ) {
          return messageContext.changeApplyStatus.runSqlMigration;
        }

        if (
          change.termCommand &&
          messageContext.changeApplyStatus.terminalCommands &&
          messageContext.changeApplyStatus.terminalCommands?.[
            change.termCommand
          ]
        ) {
          return messageContext.changeApplyStatus.terminalCommands[
            change.termCommand
          ];
        }

        if (
          change.path &&
          messageContext.changeApplyStatus.fileChanges &&
          messageContext.changeApplyStatus.fileChanges[change.path]
        ) {
          return messageContext.changeApplyStatus.fileChanges[change.path];
        }

        if (
          change.storyboardName &&
          messageContext.changeApplyStatus.storyboardChanges &&
          messageContext.changeApplyStatus.storyboardChanges[
            change.storyboardName
          ]
        ) {
          return messageContext.changeApplyStatus.storyboardChanges[
            change.storyboardName
          ];
        }
      }

      // Old behaviour, only apply to old messages, so there wont be generating or pending statuses
      if (
        messageContext?.hadError ||
        messageContext?.chatOnlyMode ||
        messageContext?.filesWithErrors?.includes?.(change?.path || "")
      ) {
        return { status: "failed", reason: "", fixing: false };
      }

      if (savedActivity) {
        return { status: "succeeded" };
      }

      return { status: "unknown" };
    };

    const handleContinue = useCallback(
      (prompt: string) => {
        // Get the current conversation messages
        const conversationMessages = Object.values(aiChatMessages)
          .filter((msg) => msg.conversation_id === selectedConversationId)
          .sort(
            (a, b) =>
              new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime(),
          );

        sendMessage({
          userPrompt: prompt,
          sendChatMessageFiles: [],
          refreshAfterResponse: true,
          chatOnlyMode: false,
          isFreePrompt: false,
          useReasoning: false,
          useSearch: false,
          codeGenerationModel: codeGenerationModel,
          messagesCanSummarize: conversationMessages.slice(0, -1),
          messagesDoNotSummarize: conversationMessages.slice(-1),
          agentChoice: agent,
        });
      },
      [
        sendMessage,
        codeGenerationModel,
        agent,
        aiChatMessages,
        selectedConversationId,
      ],
    );

    return (
      <div className="flex flex-col">
        {combinedFileChanges && Array.isArray(combinedFileChanges)
          ? combinedFileChanges.map((change, index) => {
              let textDisplay = "";
              if (change?.path) {
                textDisplay = change?.path;
              } else if (change?.termCommand) {
                textDisplay = change.termCommand;
              } else if (change?.storyboardName) {
                textDisplay = `Create ${change.storyboardName}`;
              } else if (change?.runSqlMigration) {
                textDisplay = "Running SQL Migration";
              } else if (change?.deployEdgeFunction) {
                textDisplay = "Deploying Edge Function";
              } else if (change?.runConvexSync) {
                textDisplay = "Syncing Convex DB";
              }

              const status = getChangeStatus(change, index);
              const hasErrorInfo =
                status.status === "failed" &&
                status.reason &&
                status.reason !== "" &&
                messageContext?.changeApplyStatus &&
                !status.fixing;

              return (
                <div className="flex flex-col gap-0" key={index}>
                  <div className="flex flex-row gap-1 items-center justify-start py-1 px-1">
                    <ChangeStatus changeStatus={status} />
                    <div
                      className={cn(
                        "w-full rounded-md text-2xs py-1 bg-secondary-n-950 text-secondary-n-400 flex flex-col items-start justify-start",
                        {
                          "border border-secondary-n-900 px-2 py-1":
                            change?.termCommand,
                          hidden:
                            !change?.path &&
                            !change?.termCommand &&
                            !change?.storyboardName &&
                            !change?.runSqlMigration &&
                            !change?.runConvexSync &&
                            !change?.deployEdgeFunction,
                        },
                      )}
                    >
                      <div className="w-full grid grid-cols-[1fr_auto] gap-1 items-center">
                        {change?.path || change?.storyboardName ? (
                          <button
                            className="truncate text-xs hover:text-secondary-n-200 cursor-pointer text-left"
                            onClick={() => openAiChatEngSpec(change)}
                          >
                            {textDisplay}
                          </button>
                        ) : (
                          <span className="truncate text-xs">
                            {textDisplay}
                          </span>
                        )}
                        <div className="flex items-center">
                          <StoryCodeActionButtons filePath={change?.path} />
                          {!!(change?.path || change?.storyboardName) && (
                            <FilepathLink
                              onClick={() => openAiChatEngSpec(change)}
                              ariaLabel={`Open ${
                                change?.path || change?.storyboardName || "file"
                              } in editor`}
                              tooltipContent={
                                change?.path || change?.storyboardName
                              }
                            />
                          )}
                        </div>
                      </div>
                      {messageContext?.chatOnlyMode && (
                        <span>Chat Only Mode - No Change</span>
                      )}
                    </div>
                  </div>
                  {hasErrorInfo && (
                    <div className="text-red-500 font-mono pl-8 pr-4 pb-1 text-2xs flex flex-col gap-1">
                      <div>{status.reason}</div>
                      {status.isOutputTokenLimitHit && (
                        <div>
                          <button
                            className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                            onClick={() => {
                              const prompt =
                                status.continuationPrompt ||
                                `Continue implementing changes for ${textDisplay}`;
                              handleContinue(prompt);
                            }}
                          >
                            Regenerate File
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          : null}
      </div>
    );
  },
);
